import i18next from 'i18next';

import { ISubscriptionWellfunnel } from 'api/components/Subscriptions/types';

import { DAYS_PER_MONTH, DAYS_PER_SIX_MONTH, DAYS_PER_THREE_MONTH, DAYS_PER_YEAR } from 'constants/periods';

import { getFormatedDate } from 'helpers/getFormatedDate';
import { getCurrenciesSign, getPriceWithDecimals } from 'helpers/prices';

import { SuccessModalPropsType } from 'pages/account/components/SubscriptionsTab/modals/SuccessModal/types';

import { IProductWF, IUpcomingProductWF, Product, SubscriptionProductTypes } from 'types/product';
import { CancelFlowOffers, IDiscountSubscription, SubscriptionPauseType } from 'types/subscription';

import CheckInCircleIcon from 'assets/emoji/check_in_circle.svg?url';
import check_mark from 'assets/emoji/check_mark.webp';
import ErrorIcon from 'assets/emoji/exclamation2.svg?url';

export const isMainSubscription = (productCodeType: ISubscriptionWellfunnel['product_type']): boolean =>
    [SubscriptionProductTypes.MainSubscription, SubscriptionProductTypes.UpgradeSubscription].includes(productCodeType);

export const getSubscriptionData = ({
    subscription_period,
    amount,
    currency,
}: IDiscountSubscription | (IProductWF & { subscription_period: number })) => ({
    period: subscription_period,
    type: i18next.t('subscription.type.subscription'),
    price: getPriceWithDecimals({ price: amount, currency }),
});

export const getSubscriptionSubtitle = (subscription_period: number) => {
    switch (subscription_period) {
        case DAYS_PER_MONTH:
            return 'subscription.successModal.newSubscription.subtitle.monthly';

        case DAYS_PER_THREE_MONTH:

        case DAYS_PER_SIX_MONTH:
            return 'subscription.successModal.newSubscription.subtitle.moreThanOneMonth';

        case DAYS_PER_YEAR:
            return 'subscription.successModal.newSubscription.subtitle.annual';

        default:
            return 'subscription.successModal.newSubscription.subtitle.monthly';
    }
};

export const getChangeSubscriptionSuccessModalData = ({
    amount,
    currency,
    subscription_period,
}: IUpcomingProductWF | (Product & { subscription_period: number })): SuccessModalPropsType => ({
    title: i18next.t(
        subscription_period === DAYS_PER_YEAR
            ? 'subscription.successModal.canceled.title.annual'
            : 'subscription.successModal.canceled.title',
        {
            period: subscription_period,
        }
    ),
    subtitle: i18next.t(getSubscriptionSubtitle(subscription_period), {
        currencySign: getCurrenciesSign(currency),
        price: getPriceWithDecimals({ price: amount, currency }),
        period: subscription_period,
    }),
    buttonText: 'basics.done',
    image: { src: CheckInCircleIcon },
});

export const getCancelSubscriptionSuccessModalData = (
    subscriptions: ISubscriptionWellfunnel[]
): SuccessModalPropsType => {
    const [firstSub, secondSub] = subscriptions;

    const {
        targetMonth: targetMonth1,
        targetDay: targetDay1,
        targetYear: targetYear1,
    } = getFormatedDate(firstSub.expired_date);

    const getTitleAndSubtitle = () => {
        if (secondSub) {
            const {
                targetMonth: targetMonth2,
                targetDay: targetDay2,
                targetYear: targetYear2,
            } = getFormatedDate(secondSub.expired_date);

            return {
                title: i18next.t('subscription.successModal.cancelSubscription.multiple.title', {
                    firstCode: firstSub.product_code,
                    secondCode: secondSub.product_code,
                }),
                subtitle: i18next.t('subscription.successModal.cancelSubscription.multiple.subtitle', {
                    firstCode: firstSub.product_code,
                    secondCode: secondSub.product_code,
                    targetMonth1,
                    targetDay1,
                    targetYear1,
                    targetMonth2,
                    targetDay2,
                    targetYear2,
                }),
            };
        } else {
            return {
                title: i18next.t('subscription.successModal.cancelSubscription.title', {
                    product_code: firstSub.product_code,
                }),
                subtitle: i18next.t('subscription.successModal.cancelSubscription.subtitle', {
                    targetMonth: targetMonth1,
                    targetDay: targetDay1,
                    targetYear: targetYear1,
                }),
            };
        }
    };

    const { title, subtitle } = getTitleAndSubtitle();

    return {
        title,
        subtitle,
        buttonText: 'basics.continue',
    };
};

export const changeSubscriptionErrorModalData = {
    img: { src: ErrorIcon },
    title: 'subscription.discount.error.title',
    subtitle: 'subscription.discount.error.subtitle',
    subject: 'subscription.changeSubscription.error.title',
};

export const getCancelSubscriptionErrorModalData = ({
    isSingleError,
    subscriptionsCancellationFailedPartially,
}: {
    isSingleError: boolean;
    subscriptionsCancellationFailedPartially: boolean;
}) => ({
    img: { src: ErrorIcon },
    title: isSingleError ? 'subscription.cancel.error.title.single' : 'subscription.cancel.error.title.multi',
    subject: 'subscription.cancel.error.title',
    subtitle: isSingleError ? 'subscription.cancel.error.subtitle.single' : 'subscription.cancel.error.subtitle.multi',
    wasSecondCanceled: subscriptionsCancellationFailedPartially,
});

export const getRequestResumeSubscriptionErrorModalData = (externalIds: string[]) => ({
    img: { src: ErrorIcon },
    title: 'subscription.errorModal.wasntUpdated.title',
    subtitle: 'subscription.errorModal.wasntUpdated.subtitle',
    subscriptionId: externalIds,
    offer: CancelFlowOffers.Pause,
});

export const getRestoreSuccessModalData = ({
    amount,
    currency,
    subscription_period,
}: (IProductWF & { subscription_period: number }) | IUpcomingProductWF): SuccessModalPropsType => ({
    title: 'subscription.successRestoreModal.title',
    image: { src: check_mark },
    subtitle: i18next.t('subscription.restoreModal.subscriptionPeriod', {
        period: subscription_period,
        currencySign: getCurrenciesSign(currency),
        price: getPriceWithDecimals({ price: amount, currency }),
    }),
    buttonText: 'basics.continue',
});

export const getFreeMonthSuccessModalData = (expiredDate: string) => {
    const nextMonthDate = new Date(expiredDate);

    const formattedDate = nextMonthDate
        .toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
        })
        .replace(/\//g, '-');

    return {
        title: 'subscription.successModal.freeMonth.title',
        image: { src: CheckInCircleIcon },
        subtitle: i18next.t('subscription.successModal.freeMonth.subttile', {
            date: formattedDate,
        }),
        buttonText: 'basics.continue',
        offer: CancelFlowOffers.FreeMonth,
    };
};

export const pauseSuccessModalData = {
    image: { src: CheckInCircleIcon },
    title: 'subscription.successModal.pause.title',
    buttonText: 'basics.gotIt',
    offer: CancelFlowOffers.Pause,
};

export const getResumeSuccessModalData = (date: string) => {
    const formattedDate = new Date(date)
        .toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
        })
        .replace(/\//g, '-');

    return {
        image: { src: CheckInCircleIcon },
        title: 'subscription.successModal.resume.title',
        subtitle: i18next.t('subscription.successModal.freeMonth.subttile', {
            date: formattedDate,
        }),
        buttonText: 'basics.gotIt',
    };
};

export const reminderSuccessModalData = {
    image: { src: CheckInCircleIcon },
    title: 'subscription.successModal.reminder.title',
    subtitle: 'subscription.successModal.reminder.subtitle',
    buttonText: 'basics.gotIt',
};

export const setReminderErrorModalData = {
    img: { src: ErrorIcon },
    title: 'basics.appError',
};

export const getRequestPauseSubscriptionWaitingModalData = (pauseType: SubscriptionPauseType) => ({
    title:
        pauseType === SubscriptionPauseType.Pause
            ? 'subscription.waitingModal.pause.title'
            : 'subscription.waitingModal.updatingPlan.title',
    img: null,
});

export const getRequestPauseSubscriptionErrorModalData = (externalIds: string[], pauseType: SubscriptionPauseType) => ({
    img: { src: ErrorIcon },
    title: 'subscription.errorModal.wasntUpdated.title',
    subtitle: 'subscription.errorModal.wasntUpdated.subtitle',
    subscriptionId: externalIds,
    offer: pauseType === SubscriptionPauseType.Pause ? CancelFlowOffers.Pause : CancelFlowOffers.FreeMonth,
});
